import React from 'react';
import { useParams } from 'react-router-dom';
import { gql, useMutation, useQuery } from '@apollo/client';
import { Spinner } from 'equasion-lib';
import ApiError from '@/lib/shared-coms/ApiError';
import { useRootStore } from '@/lib/providers/RootProvider';
import ReturnReceive from './ReturnReceive';
import type { RmaReceiveScanInput } from './rmaScan';

// Plain strings rather than a fragment so scripts/gql-frontend-validate.cjs resolves them against
// the schema.
const RETURN_RECEIVE_FIELDS = `
  id
  clientReturnNumber
  status
  statusLabel
  receivingScanRequired
  receivingShortfall
  client {
    id
    companyName
  }
  originalOrder {
    id
    vendorSalesOrderNumber
  }
  receivingLines {
    itemId
    productId
    product {
      id
      title
      fdaProduct {
        id
        name
        elevenDigitNdc
        deaSchedule
      }
      upcProduct {
        id
        upc
      }
    }
    rmaQuantity
    scannedQuantity
    receivedQuantity
    shortQuantity
    serialized
    expectedSerialNumbers
    shippedLots
  }
  receivingScans {
    id
    itemId
    serialNumber
    gtin
    code
    lot
    quantity
    scannedAt
  }
`;

export const WAREHOUSE_RETURN_RECEIVE = gql`
  query warehouseReturnReceive($id: ID!) {
    clientReturn(id: $id) {
      ${RETURN_RECEIVE_FIELDS}
    }
  }
`;

export const RETURN_RECEIVE_SCAN = gql`
  mutation warehouseReturnReceiveScan(
    $id: ID!
    $vendorId: ID!
    $scan: ClientReturnReceiveScanInput!
  ) {
    clientReturnReceiveScan(id: $id, vendorId: $vendorId, scan: $scan) {
      ${RETURN_RECEIVE_FIELDS}
    }
  }
`;

export const RETURN_RECEIVE_SCAN_REMOVE = gql`
  mutation warehouseReturnReceiveScanRemove($id: ID!, $vendorId: ID!, $scanId: ID!) {
    clientReturnReceiveScanRemove(id: $id, vendorId: $vendorId, scanId: $scanId) {
      ${RETURN_RECEIVE_FIELDS}
    }
  }
`;

export const RETURN_RECEIVE_COMPLETE = gql`
  mutation warehouseReturnReceiveComplete($id: ID!, $vendorId: ID!, $note: String) {
    clientReturnCompleteReceiving(id: $id, vendorId: $vendorId, note: $note) {
      ${RETURN_RECEIVE_FIELDS}
    }
  }
`;

function ReturnReceiveData() {
  const { id = '' } = useParams();
  const vendorId = useRootStore((state) => state.selectedCompany?.id) as string;
  const { data, loading, error } = useQuery(WAREHOUSE_RETURN_RECEIVE, {
    variables: { id },
    fetchPolicy: 'network-only'
  });
  const [scanMutation] = useMutation(RETURN_RECEIVE_SCAN);
  const [removeMutation] = useMutation(RETURN_RECEIVE_SCAN_REMOVE);
  const [completeMutation] = useMutation(RETURN_RECEIVE_COMPLETE);

  if (loading) return <Spinner />;
  if (error) return <ApiError error={error} />;
  const clientReturn = data?.clientReturn;
  if (!clientReturn) return <ApiError error={{ message: 'Return not found.' }} />;

  // Each mutation answers with the whole return, so the Apollo cache redraws the screen.
  return (
    <ReturnReceive
      clientReturn={clientReturn}
      onScan={(scan: RmaReceiveScanInput) =>
        scanMutation({ variables: { id, vendorId, scan } })
      }
      onRemoveScan={(scanId: string) =>
        removeMutation({ variables: { id, vendorId, scanId } })
      }
      onComplete={(note: string | null) =>
        completeMutation({ variables: { id, vendorId, note } })
      }
    />
  );
}

export default ReturnReceiveData;
